import React from 'react';
import { Loader2, CheckCircle, AlertCircle, BarChart3, ArrowRight } from 'lucide-react';

function getScoreColor(pct) {
  if (pct >= 80) return 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-700';
  if (pct >= 50) return 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-700';
  return 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-700';
}

function getBarColor(pct) {
  if (pct >= 80) return 'bg-emerald-500';
  if (pct >= 50) return 'bg-amber-500';
  return 'bg-red-500';
}

function MissingBadge({ label }) {
  return (
    <span className="font-body text-[10px] bg-card border border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded-full">
      {label}
    </span>
  );
}

function EditLink({ tab, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-0.5 text-[10px] font-body font-medium text-primary hover:text-primary/80 transition-colors"
      data-testid={'edit-link-' + tab.replace(/\s+/g, '-').toLowerCase()}
    >
      Edit <ArrowRight className="w-2.5 h-2.5" />
    </button>
  );
}

function CategoryItem({ tab, missingFields, filledCount, totalCount, onEdit }) {
  var hasMissing = missingFields.length > 0;
  var wrapClass = hasMissing
    ? 'bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-2.5'
    : 'bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg p-2.5';
  var iconColor = hasMissing ? 'text-amber-700 dark:text-amber-400' : 'text-emerald-700 dark:text-emerald-400';
  var testId = 'checklist-' + tab.replace(/\s+/g, '-').toLowerCase();

  return (
    <div className={wrapClass} data-testid={testId}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          {hasMissing ? <AlertCircle className="w-3 h-3 text-amber-600" /> : <CheckCircle className="w-3 h-3 text-emerald-600" />}
          <span className={'font-body text-xs font-semibold ' + iconColor}>{tab}</span>
          <span className="font-body text-[10px] text-muted-foreground">{filledCount}/{totalCount}</span>
        </div>
        {hasMissing && onEdit ? <EditLink tab={tab} onClick={function() { onEdit(tab); }} /> : null}
      </div>
      {hasMissing ? (
        <div className="flex flex-wrap gap-1">
          {missingFields.map(function(f) { return <MissingBadge key={f.key} label={f.label} />; })}
        </div>
      ) : (
        <p className="font-body text-[10px] text-emerald-600 dark:text-emerald-400">All fields complete</p>
      )}
    </div>
  );
}

export function FormChecklist({ scoreObj, hasCase, checklist, checklistLoading, onNavigateToTab }) {
  if (!hasCase || !scoreObj) {
    return (
      <div className="p-3 bg-primary/5 rounded-lg flex items-start gap-2">
        <CheckCircle className="w-4 h-4 text-primary mt-0.5" />
        <p className="font-body text-xs text-muted-foreground">
          Select a case to see which fields will be auto-filled and which are missing.
        </p>
      </div>
    );
  }

  var pct = checklist ? checklist.overall_score : scoreObj.score;
  var filled = checklist ? checklist.filled_count : scoreObj.filled;
  var total = checklist ? checklist.total_count : scoreObj.total;
  var cats = checklist ? checklist.categories : [];

  return (
    <div data-testid="form-score-detail">
      <div className="flex items-center gap-2 mb-2">
        <span className={'font-body text-xs font-semibold px-2 py-0.5 rounded border ' + getScoreColor(pct)}>
          <BarChart3 className="w-3 h-3 inline mr-1" />{pct}% fillable
        </span>
        <span className="text-xs text-muted-foreground">{filled}/{total} fields</span>
      </div>
      <div className="w-full h-2 bg-muted rounded-full overflow-hidden mb-3">
        <div className={'h-full rounded-full transition-all ' + getBarColor(pct)} style={{ width: pct + '%' }} />
      </div>

      {checklistLoading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading checklist...
        </div>
      ) : null}

      {!checklistLoading && cats.length > 0 ? (
        <div className="space-y-2 max-h-[280px] overflow-y-auto" data-testid="checklist-categories">
          {cats.map(function(c) {
            return (
              <CategoryItem
                key={c.tab}
                tab={c.tab}
                missingFields={c.missing_fields}
                filledCount={c.filled_count}
                totalCount={c.total_count}
                onEdit={onNavigateToTab}
              />
            );
          })}
        </div>
      ) : null}

      {!checklistLoading && cats.length === 0 && pct === 100 ? (
        <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3 flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-emerald-600" />
          <span className="font-body text-xs text-emerald-700 dark:text-emerald-400 font-medium">All fields will be auto-filled!</span>
        </div>
      ) : null}
    </div>
  );
}
